import { useState, useRef, type FormEvent } from "react";
import { m } from "framer-motion";
import { Send, CheckCircle, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { optimizedViewport } from "@/lib/motion";

const WEB3FORMS_KEY = "8dec7eea-1807-4be3-a97a-4bc25d6e10d4";
const RATE_LIMIT_MS = 15_000; // 15 seconds between submissions

const contactSchema = z.object({
  name: z.string().trim().min(1, "required").max(50, "maxLength"),
  email: z.string().trim().email("invalidEmail").max(127, "maxLength"),
  phone: z.string().trim().max(15, "maxLength").optional().or(z.literal("")),
  message: z.string().trim().min(1, "required").max(1500, "maxLength"),
});

const ContactForm = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [honeypot, setHoneypot] = useState("");
  const lastSubmitRef = useRef(0);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Honeypot check
    if (honeypot) return;

    // Rate limiting
    const now = Date.now();
    if (now - lastSubmitRef.current < RATE_LIMIT_MS) {
      alert(t("contact.rateLimited", "Počkejte prosím před dalším odesláním."));
      return;
    }

    // Validate with zod
    const result = contactSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message === "required"
          ? t("contact.fieldRequired", "Toto pole je povinné.")
          : err.message === "invalidEmail"
            ? t("contact.invalidEmail", "Neplatná e-mailová adresa.")
            : t("contact.fieldTooLong", "Příliš dlouhý vstup.");
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    setLoading(true);
    lastSubmitRef.current = now;

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `${t("contact.subject")} ${result.data.name}`,
          from_name: result.data.name,
          name: result.data.name,
          email: result.data.email,
          phone: result.data.phone || "",
          message: result.data.message,
          botcheck: "",
        }),
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
      } else {
        alert(t("contact.errorSend"));
      }
    } catch {
      alert(t("contact.errorGeneral"));
    } finally {
      setLoading(false);
    }
  };

  const fieldError = (field: string) =>
    errors[field] ? <p className="text-destructive text-xs mt-1">{errors[field]}</p> : null;

  if (submitted) {
    return (
      <m.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass rounded-2xl p-8 md:p-10 text-center">
        <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
        <h3 className="font-heading text-2xl font-bold mb-2">{t("contact.successTitle")}</h3>
        <p className="text-muted-foreground mb-6">{t("contact.successText")}</p>
        <Button variant="outline" onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", message: "" }); }}>{t("contact.sendAnother")}</Button>
      </m.div>
    );
  }

  return (
    <m.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={optimizedViewport} transition={{ duration: 0.5 }} className="glass rounded-2xl p-8 md:p-10">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Honeypot - hidden from real users */}
        <div className="absolute opacity-0 -z-10 h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
          <Input type="text" name="botcheck" autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="contact-name" className="block text-sm font-medium mb-1.5">{t("contact.name")} *</label>
            <Input id="contact-name" name="name" required maxLength={50} placeholder={t("contact.namePlaceholder")} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            {fieldError("name")}
          </div>
          <div>
            <label htmlFor="contact-email" className="block text-sm font-medium mb-1.5">{t("contact.emailLabel")} *</label>
            <Input id="contact-email" name="email" type="email" required maxLength={127} placeholder={t("contact.emailPlaceholder")} value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            {fieldError("email")}
          </div>
        </div>
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium mb-1.5">{t("contact.phone")}</label>
          <Input id="contact-phone" name="phone" type="tel" maxLength={15} placeholder={t("contact.phonePlaceholder")} value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
          {fieldError("phone")}
        </div>
        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium mb-1.5">{t("contact.message")} *</label>
          <Textarea id="contact-message" name="message" required rows={5} maxLength={1500} placeholder="" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} />
          {fieldError("message")}
        </div>
        <Button type="submit" variant="hero" size="lg" className="w-full" disabled={loading}>
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {loading ? t("contact.sending") : t("contact.submit")}
        </Button>
      </form>
    </m.div>
  );
};

export default ContactForm;
